import { AmigoDinoColor } from '@theme/amigo-dino-color'
import { AmigoDinoScroll } from '@theme/amigo-dino-scroll'
import { Component } from '@theme/component'
import { onRawPreloaderReady } from '@theme/events'
import { GLTFLoader } from '@theme/gltf-loader'
import { useGsap } from '@theme/gsap'
import { MeshoptDecoder } from '@theme/meshopt-decoder'
import { isMobile } from '@theme/use-device-detection'
import * as THREE from 'three'

const amigoDinoConfig = {
  camera: {
    fov: 45,
    position: {
      x: 20,
      y: 35,
      z: 280
    }
  },
  mobileModelScale: 0.8,
  pixelRatioCap: 2,
  hiddenScale: 0.001,
  lights: {
    ambientIntensity: 0.12,
    mainLightIntensity: 4.2,
    sunTemperature: 6000,
    sunElevation: 45,
    sunAzimuth: 35,
    sunDistance: 260,
    fillLightIntensity: 0.2,
    hemiSkyColor: '#cfe8ff',
    hemiGroundColor: '#e7dbc5',
    hemiLightIntensity: 0.45
  },
  material: {
    roughness: 0.115,
    metalness: 0.213
  },
  reveal: {
    duration: 1.2,
    delay: 0.06,
    ease: 'elastic.out(1, 0.75)'
  },
  pointer: {
    duration: 0.6,
    ease: 'power3'
  }
}

function kelvinToColor(kelvin) {
  const temp = THREE.MathUtils.clamp(kelvin, 1000, 40000) / 100
  let red
  let green
  let blue

  if (temp <= 66) {
    red = 255
    green = 99.4708025861 * Math.log(temp) - 161.1195681661
    blue =
      temp <= 19 ? 0 : 138.5177312231 * Math.log(temp - 10) - 305.0447927307
  } else {
    red = 329.698727446 * (temp - 60) ** -0.1332047592
    green = 288.1221695283 * (temp - 60) ** -0.0755148492
    blue = 255
  }

  const c = THREE.MathUtils.clamp
  return new THREE.Color(
    c(red, 0, 255) / 255,
    c(green, 0, 255) / 255,
    c(blue, 0, 255) / 255
  )
}

class AmigoDino extends Component {
  requiredRefs = ['canvas']

  #activeRenderRequests = 0
  #isMobileViewport = false
  #rawPreloaderReadyCleanup = null
  #renderLoopActive = false

  mounted() {
    void this.#init()
  }

  unmounted() {
    this.#rawPreloaderReadyCleanup?.()
    this.#rawPreloaderReadyCleanup = null
    this.abortController?.abort()
    this.abortController = null
    this.scroll?.destroy()
    this.scroll = null
    this.color?.destroy()
    this.color = null
    if (this.scope) {
      this.#stopRenderLoop()
      this.scope.context.revert()
      this.scope = null
    }
    this.#activeRenderRequests = 0
    this.#disposeScene()
  }

  async #init() {
    this.#isMobileViewport = isMobile()

    const scope = await useGsap(
      this,
      undefined,
      this.#isMobileViewport ? [] : ['ScrollTrigger']
    )
    if (!(scope && this.isConnected)) {
      scope?.context.revert()
      return
    }
    this.scope = scope

    this.baseRotX = Number(this.dataset.rotX)
    this.baseRotY = Number(this.dataset.rotY)
    this.baseRotZ = Number(this.dataset.rotZ)
    this.modelScale = this.#isMobileViewport
      ? amigoDinoConfig.mobileModelScale
      : Number(this.dataset.modelScale)
    this.#measureViewport()
    this.#setupScene()

    await this.#loadModel()
    if (!this.isConnected) {
      this.#disposeScene()
      return
    }

    this.abortController = new AbortController()
    this.group.scale.setScalar(amigoDinoConfig.hiddenScale)
    window.addEventListener('resize', this.onResize, {
      signal: this.abortController.signal
    })
    this.render()
    this.#rawPreloaderReadyCleanup = onRawPreloaderReady(() => {
      if (!(this.isConnected && this.scope === scope)) return
      scope.contextSafe(() => {
        this.#playIntro()
        this.#bindPointer()
        this.#bindScroll()
        this.#bindColor()
      })
    })
  }

  #measureViewport() {
    const bounds = this.getBoundingClientRect()
    this.width = bounds.width || window.innerWidth
    this.height = bounds.height || window.innerHeight
  }

  #setupScene() {
    this.scene = new THREE.Scene()
    this.#setupGroups()
    this.#setupCamera()
    this.#setupRenderer()
    this.#setupLights()
  }

  #setupGroups() {
    this.group = new THREE.Group()
    this.pointerGroup = new THREE.Group()
    this.modelRoot = new THREE.Group()
    this.group.add(this.pointerGroup)
    this.pointerGroup.add(this.modelRoot)
    this.scene.add(this.group)
  }

  #setupCamera() {
    const { fov, position } = amigoDinoConfig.camera
    this.camera = new THREE.PerspectiveCamera(
      fov,
      this.width / this.height,
      0.1,
      1000
    )
    this.camera.position.set(position.x, position.y, position.z)
    this.camera.lookAt(0, 0, 0)
  }

  #setupRenderer() {
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.refs.canvas,
      antialias: true,
      alpha: true
    })
    this.renderer.setPixelRatio(
      Math.min(window.devicePixelRatio, amigoDinoConfig.pixelRatioCap)
    )
    this.renderer.setSize(this.width, this.height)
    this.renderer.toneMapping = THREE.LinearToneMapping
    this.renderer.outputColorSpace = THREE.SRGBColorSpace
  }

  #setupLights() {
    const lights = amigoDinoConfig.lights
    const ambient = new THREE.AmbientLight(0xffffff, lights.ambientIntensity)

    const mainLight = new THREE.DirectionalLight(
      0xffffff,
      lights.mainLightIntensity
    )
    mainLight.color.copy(kelvinToColor(lights.sunTemperature))
    const elevationRad = THREE.MathUtils.degToRad(lights.sunElevation)
    const azimuthRad = THREE.MathUtils.degToRad(lights.sunAzimuth)
    mainLight.position.set(
      lights.sunDistance * Math.cos(elevationRad) * Math.sin(azimuthRad),
      lights.sunDistance * Math.sin(elevationRad),
      lights.sunDistance * Math.cos(elevationRad) * Math.cos(azimuthRad)
    )
    const mainLightTarget = new THREE.Object3D()
    mainLight.target = mainLightTarget

    const fillLight = new THREE.DirectionalLight(
      0xffffff,
      lights.fillLightIntensity
    )
    fillLight.position.set(-15, 0, -15)

    const hemiLight = new THREE.HemisphereLight(
      lights.hemiSkyColor,
      lights.hemiGroundColor,
      lights.hemiLightIntensity
    )
    hemiLight.position.set(0, 50, 0)

    this.scene.add(ambient)
    this.scene.add(mainLight)
    this.scene.add(mainLightTarget)
    this.scene.add(fillLight)
    this.scene.add(hemiLight)
  }

  async #loadModel() {
    const loader = new GLTFLoader()
    loader.setMeshoptDecoder(MeshoptDecoder)
    const gltf = await loader.loadAsync(this.dataset.modelSrc)
    const model = gltf.scene
    const bounds = new THREE.Box3().setFromObject(model)
    model.position.sub(bounds.getCenter(new THREE.Vector3()))
    this.#configureModelMaterials(model)
    this.modelRoot.rotation.set(this.baseRotX, this.baseRotY, this.baseRotZ)
    this.modelRoot.scale.setScalar(this.modelScale)
    this.modelRoot.add(model)
  }

  #configureModelMaterials(model) {
    const { roughness, metalness } = amigoDinoConfig.material
    model.traverse((node) => {
      if (!(node.isMesh && node.material)) return
      const originals = Array.isArray(node.material)
        ? node.material
        : [node.material]
      node.material = new THREE.MeshStandardMaterial({
        flatShading: true,
        roughness,
        metalness
      })
      for (const mat of originals) mat?.dispose?.()
    })
  }

  #playIntro() {
    const { hiddenScale, reveal } = amigoDinoConfig
    this.#requestTemporaryRenderLoop()
    this.scope.gsap.fromTo(
      this.group.scale,
      { x: hiddenScale, y: hiddenScale, z: hiddenScale },
      {
        x: 1,
        y: 1,
        z: 1,
        ease: reveal.ease,
        duration: reveal.duration,
        delay: reveal.delay,
        onComplete: () => this.#releaseTemporaryRenderLoop(),
        onInterrupt: () => this.#releaseTemporaryRenderLoop()
      }
    )
  }

  #bindPointer() {
    if (this.#isMobileViewport) return
    this.#startRenderLoop()
    const tween = amigoDinoConfig.pointer
    this.follow = {
      x: this.scope.gsap.quickTo(this.pointerGroup.position, 'x', tween),
      y: this.scope.gsap.quickTo(this.pointerGroup.position, 'y', tween),
      rotX: this.scope.gsap.quickTo(this.pointerGroup.rotation, 'x', tween),
      rotY: this.scope.gsap.quickTo(this.pointerGroup.rotation, 'y', tween),
      rotZ: this.scope.gsap.quickTo(this.pointerGroup.rotation, 'z', tween)
    }
    window.addEventListener('mousemove', this.onPointerMove, {
      signal: this.abortController.signal
    })
  }

  #bindScroll() {
    if (this.#isMobileViewport || !globalThis.ScrollTrigger) return
    this.#startRenderLoop()
    this.scroll = AmigoDinoScroll({
      gsap: this.scope.gsap,
      host: this,
      trigger: this.closest('[data-hero-model-scrolling]') || this,
      pointerGroup: this.pointerGroup,
      modelRoot: this.modelRoot,
      baseRotX: this.baseRotX,
      modelScale: this.modelScale,
      hiddenScale: amigoDinoConfig.hiddenScale
    })
    this.scroll.mount()
  }

  #bindColor() {
    const variations = this.#parseVariations()
    if (variations.length === 0) return
    const root =
      this.closest('[data-hero-model-scrolling]') ||
      this.parentElement ||
      document
    this.color = AmigoDinoColor({
      gsap: this.scope.gsap,
      group: this.group,
      modelRoot: this.modelRoot,
      variations,
      prevBtn: root.querySelector('[data-amigo-dino-color="prev"]'),
      nextBtn: root.querySelector('[data-amigo-dino-color="next"]'),
      signal: this.abortController.signal,
      onRenderEnd: () => this.#releaseTemporaryRenderLoop(),
      onRenderStart: () => this.#requestTemporaryRenderLoop()
    })
    this.color.mount()
  }

  #startRenderLoop() {
    if (this.#renderLoopActive || !this.scope) return
    this.scope.gsap.ticker.add(this.render)
    this.#renderLoopActive = true
  }

  #stopRenderLoop() {
    if (!(this.#renderLoopActive && this.scope)) return
    this.scope.gsap.ticker.remove(this.render)
    this.#renderLoopActive = false
  }

  #requestTemporaryRenderLoop() {
    this.#activeRenderRequests += 1
    this.#startRenderLoop()
  }

  #releaseTemporaryRenderLoop() {
    this.#activeRenderRequests = Math.max(0, this.#activeRenderRequests - 1)
    if (this.#isMobileViewport && this.#activeRenderRequests === 0) {
      this.#stopRenderLoop()
      this.render()
    }
  }

  #parseVariations() {
    const raw = this.dataset.variations
    if (!raw) return []
    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }

  #disposeScene() {
    if (this.scene) {
      this.scene.traverse((node) => {
        if (!node.isMesh) return
        node.geometry.dispose()
        node.material.dispose()
      })
      this.scene = null
    }
    this.renderer?.dispose()
    this.renderer = null
  }

  onPointerMove = (event) => {
    const nx = event.clientX / this.width - 0.5
    const ny = event.clientY / this.height - 0.5
    this.follow.x(2 * nx)
    this.follow.y(-4 * ny)
    this.follow.rotY(nx / 2)
    this.follow.rotX((ny * Math.PI) / 12)
    this.follow.rotZ(nx / 10)
  }

  onResize = () => {
    this.#measureViewport()
    this.camera.aspect = this.width / this.height
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(this.width, this.height)
    this.render()
  }

  render = () => {
    if (!(this.renderer && this.scene && this.camera)) return
    this.renderer.render(this.scene, this.camera)
  }
}

if (!customElements.get('amigo-dino')) {
  customElements.define('amigo-dino', AmigoDino)
}
